import { useEffect, useState, useRef } from 'react';

import {
    Box,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    CircularProgress,
    Snackbar,
    Alert,
} from '@mui/material';
import { DaylitLogo } from '@lendica/components';
import { PersonalInfoForm } from '../forms/PersonalInfoForm';
import { BusinessInfoForm } from '../forms/BusinessInfoForm';
import { AdditionalInfoForm } from '../forms/AdditionalInfoForm';
import { MarketingCard } from '../components/MarketingCard';
import { BreadCrumbs } from '../components/BreadCrumbs';
import { formatDate } from '../utils/validation';

import * as api from '../api/index';
import { useApplication } from '../context/useApplication';
import { pushDL } from '@lendica/api';
import { extractAllowedParams, sanitizeParams } from '../config/allowedQueryParams';

const steps = [
    { label: 'A little bit about yourself', key: 'personal' },
    { label: "What's your business like?", key: 'business' },
    { label: 'Help us qualify you for more', key: 'additional' },
];

const getIpAddress = async () => {
    const res = await fetch('https://api.ipify.org/?format=json');
    const { ip } = await res.json();
    return ip;
};

const getFingerprint = async () => {
    let { ica_fp } = window;
    if (ica_fp) {
        return ica_fp;
    }
    ica_fp = {
        // browserName: navigator.userAgent,
        os: navigator.platform,
        // screenResolution: `${screen.width}x${screen.height}`,
        // language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        geo: btoa(await getIpAddress()),
        // allowCookies: navigator.cookieEnabled,
        // doNotTrack: navigator.doNotTrack,
    };
    window.ica_fp = ica_fp;
    return ica_fp;
};

// Helper function to merge backend data with lead data (lead data overwrites backend)
const mergeApplicationData = (backendData, leadData) => {
    if (!leadData) {
        return backendData;
    }

    // Extract known lead form fields
    const { firstName, lastName, email, phone, company, goals, ...queryParams } = leadData;

    const merged = {
        ...backendData,
        // Lead data overwrites backend data
        first_name: firstName || backendData.first_name || '',
        last_name: lastName || backendData.last_name || '',
        email: email || backendData.email || '',
        phone_number: phone || backendData.phone_number || '',
        company_legal_name: company || backendData.company_legal_name || '',
        // Store goals and query params in metadata for analysis
        meta: {
            ...backendData.meta,
            lead_goals: goals || [],
            // Include any query params (UTMs, partner_name, etc.) that were captured
            ...queryParams,
        },
    };

    return merged;
};

export const ApplicationPage = ({ onComplete }) => {
    const [dataLoaded, setDataLoaded] = useState(false);
    const [savingLeadData, setSavingLeadData] = useState(false);
    const [application, setApplication] = useState({
        id: '',
        company: '', // company id
        first_name: '',
        last_name: '',
        home_address: '',
        email: '',
        password: '',
        phone_number: '',
        company_legal_name: '',
        business_ein: '',
        date_of_incorporation: '',
        ownership_percent: '',
        monthly_debt_pmt: '',
        monthly_revenue: '',
        company_address: '',
        website: '',
        ssn: '',
        personal_credit_score: '',
        date_of_birth: '',
        additional_owners: '',
        business_licenses: '',
        meta: {},
    });
    const [company, setCompany] = useState({});
    const [activeStep, setActiveStep] = useState(0);
    const { searchParams, leadData, consumeLeadData } = useApplication();

    // Use ref to track if initial data load has happened
    // This prevents double-loading in React 18 StrictMode (development)
    // where components are mounted twice to detect side effects
    const hasLoadedData = useRef(false);

    // Load backend data and merge with lead data (run once on mount)
    useEffect(() => {
        // Skip if already loaded (React 18 StrictMode protection)
        if (hasLoadedData.current) {
            return;
        }

        (async function () {
            const currentApplication = await api.getApplication();

            let companyInfo = await api.getCompany();

            if (currentApplication.date_of_incorporation) {
                currentApplication.date_of_incorporation = formatDate(
                    currentApplication.date_of_incorporation,
                    'yyyy-MM-dd'
                );
            }

            if (currentApplication.date_of_birth) {
                currentApplication.date_of_birth = formatDate(
                    currentApplication.date_of_birth,
                    'yyyy-MM-dd'
                );
            }

            if (currentApplication.status !== 0) {
                onComplete(currentApplication.status);
            }

            // Merge backend data with lead data (lead data overwrites backend)
            const mergedApplication = mergeApplicationData(currentApplication, leadData);

            setCompany(companyInfo);
            setApplication(mergedApplication);
            setDataLoaded(true);

            // Mark as loaded to prevent re-runs in StrictMode
            hasLoadedData.current = true;

            // If we have lead data, save the merged application to backend immediately
            if (leadData) {
                setSavingLeadData(true);
                try {
                    // Save merged application data
                    await api.postApplication(mergedApplication);
                } catch (error) {
                    // Continue anyway - autosave will retry later
                } finally {
                    setSavingLeadData(false);
                }

                // Clear lead data after consumption
                consumeLeadData();
            } else {
            }

            pushDL('login_success', {
                application_id: mergedApplication.id,
                company_id: mergedApplication.company,
                phone: mergedApplication.phone_number,
            });

            pushDL('application_step', {
                step: 'application_load',
                application_id: mergedApplication.id,
                company_id: mergedApplication.company,
                phone: mergedApplication.phone_number,
            });
        })();
    }, []); // Run once on mount only

    useEffect(() => {
        (async function () {
            dataLoaded && !!searchParams && (await sendApplicationParams(searchParams));
        })();
    }, [dataLoaded, searchParams]);

    const sendApplicationParams = async query => {
        const meta = {};
        const fingerprint = await getFingerprint();
        meta.fingerprint = fingerprint;

        // Extract and sanitize only allowed parameters
        const allowedParams = sanitizeParams(extractAllowedParams(query));

        // Add allowed params to meta
        Object.assign(meta, allowedParams);

        await api.postApplication({ meta });
    };

    const handleNext = () => {
        setActiveStep(prevActiveStep => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep(prevActiveStep => prevActiveStep - 1);
    };

    const handleApplicationChange = values => {
        setApplication(currentValue => ({
            ...currentValue,
            ...values,
        }));
    };

    const getStep = key => {
        if (key === 'personal') {
            return (
                <PersonalInfoForm
                    application={application}
                    handleApplicationChange={handleApplicationChange}
                    handleNext={handleNext}
                />
            );
        } else if (key === 'business') {
            return (
                <BusinessInfoForm
                    application={application}
                    handleApplicationChange={handleApplicationChange}
                    handleNext={handleNext}
                    handleBack={handleBack}
                />
            );
        } else if (key === 'additional') {
            return (
                <AdditionalInfoForm
                    application={application}
                    handleApplicationChange={handleApplicationChange}
                    handleNext={handleNext}
                    handleBack={handleBack}
                    companyId={company.company.id}
                />
            );
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: 350,
                width: '100%',
                maxWidth: 600,
                paddingX: 2,
                pt: 1,
                pb: 6,
                boxSizing: 'border-box',
            }}
        >
            <DaylitLogo height={20} />
            <BreadCrumbs page={1} company_id={company.id} />

            <MarketingCard activeStep={activeStep} page={1} />
            {dataLoaded ? (
                <Stepper activeStep={activeStep} orientation="vertical">
                    {steps.map((step, index) => (
                        <Step
                            key={step.key}
                            sx={{
                                '& .MuiStepLabel-root .Mui-completed': {
                                    color: 'success.main', // circle color (COMPLETED)
                                },
                                '& .MuiStepLabel-label.Mui-completed': {
                                    color: 'text.secondary', // Just text label (COMPLETED)
                                },
                                '& .MuiStepLabel-root .Mui-active': {
                                    color: 'text.secondary', // circle color (ACTIVE)
                                },
                            }}
                        >
                            <StepLabel>{step.label}</StepLabel>
                            <StepContent>{getStep(step.key)}</StepContent>
                        </Step>
                    ))}
                </Stepper>
            ) : (
                <Box
                    sx={{
                        height: '100%',
                        minHeight: 500,
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <CircularProgress />
                </Box>
            )}
            <Snackbar
                open={savingLeadData}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity="info" sx={{ width: '100%' }}>
                    Saving your information...
                </Alert>
            </Snackbar>
        </Box>
    );
};
